/**
 * Where the sun is, from a timestamp and a place.
 *
 * The app already gets the sun from the hub — `useSolar()` reads
 * `/api/solar/current` and the hub pushes `solar:update` every minute. But that
 * is only ever *now*, and the weather modal lets you scrub through the
 * forecast: the sky behind it has to be right for the hour you picked. Sunrise
 * and sunset walk through the year, so looking up "today's" times for a date
 * five days out would put the sun under the horizon at breakfast.
 *
 * This is the standard low-precision solar position (NOAA's equations, good to
 * about 0.5° — far finer than a sky gradient can show). Pure and
 * dependency-free, so it can be unit-tested and reused anywhere.
 */

const DEG = Math.PI / 180;
const J2000 = 2_451_545.0;
const UNIX_EPOCH_AS_JULIAN = 2_440_587.5;

export interface SolarPosition {
  /** Degrees above the horizon; negative when the sun has set. */
  altitude: number;
  /** Compass bearing in degrees, 0 = north, 90 = east. */
  azimuth: number;
}

/** Fractional days since J2000.0 for an instant. */
function daysSinceJ2000(date: Date): number {
  return date.getTime() / 86_400_000 + UNIX_EPOCH_AS_JULIAN - J2000;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * Sun altitude and azimuth at an instant, for a WGS-84 coordinate.
 *
 * @param date     the instant, in UTC (a `Date` is always UTC internally)
 * @param latitude degrees north, negative south
 * @param longitude degrees east, negative west
 */
export function solarPositionAt(date: Date, latitude: number, longitude: number): SolarPosition {
  const n = daysSinceJ2000(date);

  // Mean anomaly, then the sun's ecliptic longitude (equation of the centre).
  const meanAnomaly = (357.5291 + 0.98560028 * n) * DEG;
  const meanLongitude = 280.459 + 0.98564736 * n;
  const eclipticLongitude =
    (meanLongitude + 1.915 * Math.sin(meanAnomaly) + 0.02 * Math.sin(2 * meanAnomaly)) * DEG;

  // Obliquity of the ecliptic, giving declination and right ascension.
  const obliquity = (23.439 - 0.00000036 * n) * DEG;
  const declination = Math.asin(Math.sin(obliquity) * Math.sin(eclipticLongitude));
  const rightAscension = Math.atan2(Math.cos(obliquity) * Math.sin(eclipticLongitude), Math.cos(eclipticLongitude)) / DEG;

  // Greenwich mean sidereal time (hours) → hour angle here, folded to ±180°.
  const gmst = (((18.697374558 + 24.06570982441908 * n) % 24) + 24) % 24;
  const rawHourAngle = (gmst * 15 + longitude - rightAscension) * DEG;
  const hourAngle = Math.atan2(Math.sin(rawHourAngle), Math.cos(rawHourAngle));

  const lat = latitude * DEG;
  const sinAltitude =
    Math.sin(lat) * Math.sin(declination) + Math.cos(lat) * Math.cos(declination) * Math.cos(hourAngle);
  const altitude = Math.asin(clamp(sinAltitude, -1, 1)) / DEG;

  // Azimuth from the declination/haltitude triangle; the hour angle's sign says
  // which side of the meridian the sun is on.
  const cosAltitude = Math.cos(Math.asin(clamp(sinAltitude, -1, 1)));
  let azimuth = 180;
  if (Math.abs(cosAltitude) > 1e-6 && Math.abs(Math.cos(lat)) > 1e-6) {
    const cosAzimuth = (Math.sin(declination) - Math.sin(lat) * sinAltitude) / (Math.cos(lat) * cosAltitude);
    azimuth = Math.acos(clamp(cosAzimuth, -1, 1)) / DEG;
    if (Math.sin(hourAngle) > 0) azimuth = 360 - azimuth;
  }

  return { altitude, azimuth };
}

/** Fraction of the way from `from` to `to`, clamped to 0–1. */
export function fractionBetween(value: number, from: number, to: number): number {
  if (to === from) return 0;
  return clamp((value - from) / (to - from), 0, 1);
}

/**
 * The sky's colour anchors, keyed on sun altitude. Each band is the altitude
 * range where that look holds; `top`/`middle`/`horizon` are painted as a
 * vertical gradient, and the two opacities fade the stars and the sun disc in
 * and out rather than switching them on and off.
 *
 * Altitudes are the real ones: -6° is civil twilight, -12° nautical, +6° the
 * end of the golden hour. A scrubbed hour therefore gets the colour the sky
 * actually had at that time, not a clock-based guess.
 */
export interface SkyBand {
  /** Sun altitude this band starts at, descending. */
  from: number;
  /** Zenith colour. */
  top: string;
  /** Mid-sky colour. */
  middle: string;
  /** Colour at the horizon. */
  horizon: string;
  /** Star visibility for this band, 0–1. */
  stars: number;
  /** Sun-disc visibility for this band, 0–1. */
  sun: number;
}

/**
 * Ordered from high sun to deep night, so a lookup is a scan for the first band
 * whose `from` is at or above the altitude. Colours are picked to read as
 * "outside" rather than as UI chrome: a clean day, a warm horizon at the golden
 * hour, an ember band at civil twilight, then navy and near-black.
 */
export const SKY_BANDS: SkyBand[] = [
  { from: 45, top: '#2f80d8', middle: '#79b9ee', horizon: '#cfe6f8', stars: 0, sun: 1 },
  { from: 12, top: '#3a8edd', middle: '#8cc3f0', horizon: '#e7f0f7', stars: 0, sun: 1 },
  { from: 4, top: '#3f7fc4', middle: '#9dbde0', horizon: '#f3d9b4', stars: 0, sun: 1 },
  { from: -1, top: '#3c5a86', middle: '#8a6f85', horizon: '#e5926a', stars: 0.1, sun: 0.9 },
  { from: -6, top: '#25325a', middle: '#4d3f68', horizon: '#b9614f', stars: 0.45, sun: 0.35 },
  { from: -12, top: '#141b36', middle: '#252145', horizon: '#4a3a5c', stars: 0.8, sun: 0 },
  // Nautical dusk is where the sky stops changing: below this the stars are full
  // and the sky is as dark as it gets. (Running the last band all the way to -90
  // meant a scrubbed night hour never quite reached full night.)
  { from: -18, top: '#070b18', middle: '#0e1327', horizon: '#1b2340', stars: 1, sun: 0 },
];

/** The band-look for a sun altitude, with the two nearest bands blended so the sky never steps. */
export function skyAt(altitude: number): Omit<SkyBand, 'from'> {
  // SKY_BANDS runs high sun → deep night, so the first band whose floor is at
  // or below this altitude is the *lower* one of the enclosing pair — the band
  // above it is the previous entry. (Reading it the other way round made the
  // blend clamp at 1 for every altitude inside a band, which snapped the stars
  // on at -6° instead of fading them in.)
  const lowerIndex = SKY_BANDS.findIndex((band) => altitude >= band.from);

  // Above the highest band or below the lowest, that band simply holds.
  if (lowerIndex <= 0) return { ...SKY_BANDS[lowerIndex === 0 ? 0 : SKY_BANDS.length - 1] };

  const upper = SKY_BANDS[lowerIndex - 1];
  const lower = SKY_BANDS[lowerIndex];
  const t = fractionBetween(altitude, lower.from, upper.from);

  return {
    top: mixHex(lower.top, upper.top, t),
    middle: mixHex(lower.middle, upper.middle, t),
    horizon: mixHex(lower.horizon, upper.horizon, t),
    stars: lower.stars + (upper.stars - lower.stars) * t,
    sun: lower.sun + (upper.sun - lower.sun) * t,
  };
}

/** Linear blend of two `#rrggbb` colours; `t` is 0 at `from`, 1 at `to`. */
export function mixHex(from: string, to: string, t: number): string {
  const a = parseHex(from);
  const b = parseHex(to);
  const mix = (i: number) => Math.round(a[i] + (b[i] - a[i]) * clamp(t, 0, 1));
  return `#${[0, 1, 2].map((i) => mix(i).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Pull a colour toward its own grey — how cloud drains the blue out of the
 * sky. The grey is the colour's own luma, so the light level of the hour
 * survives and only the hue goes. `amount` runs 0 (untouched) to 1 (fully
 * grey).
 */
export function desaturateHex(hex: string, amount: number): string {
  const [r, g, b] = parseHex(hex);
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const drain = (channel: number): string =>
    Math.round(channel + (luma - channel) * clamp(amount, 0, 1))
      .toString(16)
      .padStart(2, '0');
  return `#${drain(r)}${drain(g)}${drain(b)}`;
}

function parseHex(hex: string): [number, number, number] {
  const value = hex.replace('#', '');
  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ];
}

/** Where the horizon line sits in the sky panel, as a fraction of its height. */
export const HORIZON_FRACTION = 0.72;

/** How long the moon takes to come back to the same phase, in days. */
export const SYNODIC_MONTH_DAYS = 29.530588;

/**
 * Carry a known moon phase forward (or back) to another instant.
 *
 * The phase itself comes from the hub and is only true for *now*. Scrubbing the
 * forecast four days ahead would otherwise show tonight's moon — the real one
 * advances about 1/29.5 of a cycle per day, which is a visible change over a
 * week (a half-lit moon becomes nearly full). Accurate to a few percent a week,
 * which is well inside what a phase-shaped disc can show.
 */
export function shiftMoonPhase(phase: number, from: Date, to: Date): number {
  const days = (to.getTime() - from.getTime()) / 86_400_000;
  return (((phase + days / SYNODIC_MONTH_DAYS) % 1) + 1) % 1;
}

/**
 * How much of the disc is lit at a phase, 0–100. Exact for a given phase: the
 * terminator is an ellipse whose half-width is `cos(2π·phase)`, so the lit area
 * is simply `(1 - cos 2π·phase) / 2`.
 */
export function moonIlluminationPercent(phase: number): number {
  return Math.round(((1 - Math.cos(2 * Math.PI * phase)) / 2) * 100);
}

/**
 * The sun's centre crossing this angle. -0.833° is the standard horizon dip —
 * the sun's upper limb touching the horizon, which is what "sunrise" means.
 */
const SUNRISE_ALTITUDE = -0.833;

/**
 * Sunrise and sunset for the local day containing `date`.
 *
 * The hub's `sunTimes` are today's, but the forecast can be scrubbed to another
 * day and the sun card has to follow it (four days out, sunrise is minutes later
 * and the day is shorter). The day is the *local* one, because that is the day
 * the forecast rows are labelled with. Scans at two-minute steps and bisects the
 * crossings, which lands well inside a second.
 *
 * Both are `null` where there is no crossing in the window — polar day or night.
 *
 * Accuracy, honestly: the altitude is good to about 0.5°, and near the horizon
 * the sun climbs at roughly 0.2°/minute here, so a *time* derived this way can be
 * two or three minutes out (measured against the provider: sunrise +16 s, sunset
 * -2.8 min for Koper on 2026-10-02). That is why the modal keeps showing the
 * provider's exact sunrise/sunset for today and only falls back to this for the
 * days the provider has not sent times for — where minutes do not matter anyway.
 */
export function sunTimesFor(
  date: Date,
  latitude: number,
  longitude: number,
): { sunrise: Date | null; sunset: Date | null } {
  const altitudeAt = (ms: number): number => solarPositionAt(new Date(ms), latitude, longitude).altitude;
  const crossing = (lowMs: number, highMs: number): number => {
    let low = lowMs;
    let high = highMs;
    for (let i = 0; i < 30; i += 1) {
      const middle = (low + high) / 2;
      if (altitudeAt(middle) < SUNRISE_ALTITUDE) low = middle;
      else high = middle;
    }
    return Math.round((low + high) / 2);
  };

  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  const end = start.getTime() + 86_400_000;
  const step = 120_000;

  let sunrise: number | null = null;
  let sunset: number | null = null;
  let previousAltitude = altitudeAt(start.getTime());

  for (let ms = start.getTime() + step; ms <= end; ms += step) {
    const altitude = altitudeAt(ms);
    if (sunrise === null && previousAltitude < SUNRISE_ALTITUDE && altitude >= SUNRISE_ALTITUDE) {
      sunrise = crossing(ms - step, ms);
    }
    if (sunset === null && previousAltitude >= SUNRISE_ALTITUDE && altitude < SUNRISE_ALTITUDE) {
      sunset = crossing(ms - step, ms);
    }
    previousAltitude = altitude;
    if (sunrise !== null && sunset !== null) break;
  }

  return { sunrise: sunrise === null ? null : new Date(sunrise), sunset: sunset === null ? null : new Date(sunset) };
}

/**
 * How high in the sky a body sits, as a 0–1 fraction of the panel, from its
 * altitude. The zenith is the top edge and the horizon is `HORIZON_FRACTION`
 * down; a set sun dips just below that line (it is drawn beneath the ground, so
 * it slides out of sight rather than sinking through the floor) and a body far
 * below the horizon stays pinned there.
 */
export function altitudeToPanelFraction(altitude: number): number {
  const clamped = Math.max(-1, Math.min(90, altitude));
  return HORIZON_FRACTION * (1 - clamped / 90);
}

/**
 * Horizontal position for a compass bearing, as a 0–1 fraction of the panel.
 *
 * The panel is the view you get standing outside and facing south — which is
 * where the sun spends the day in the northern hemisphere, and how every weather
 * app draws it. Facing south, **east is on your left and west on your right**, so
 * the sun rises on the left, peaks in the middle and sets on the right.
 *
 * The position follows `-sin(bearing)`, the bearing's east-west component with
 * its sign flipped for that viewing direction. That matters twice over: a
 * piecewise linear map from bearing to x is discontinuous at north (0° and 360°
 * would land at opposite edges, so the sun teleports across the panel at
 * midnight), and getting the sign wrong mirrors the whole day — the sun rises
 * on the right and sets on the left.
 */
export function azimuthToPanelFraction(azimuth: number): number {
  return 0.5 - 0.5 * Math.sin((((azimuth % 360) + 360) % 360) * DEG);
}
